import type { ButtonProps } from '@mui/material';

import * as React from 'react';
import { Switch } from '@mend/mui';
import {
  Box,
  Card,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
  useRadioGroup,
} from '@mui/material';
import { formControlLabelClasses } from '@mui/material/FormControlLabel';
import { visuallyHidden } from '@mui/utils';

const Context = React.createContext<{
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  selected: boolean;
  /** Selects this card. Interactive controls call this instead of performing their own action
   *  when the card isn't selected yet, so the first click always selects rather than acting. */
  selectCard: () => void;
} | null>(null);

function useCardContext() {
  const context = React.useContext(Context);

  if (!context) {
    throw new Error(
      'ReadinessBucketCard components must be rendered within ReadinessBucketCard.Root'
    );
  }

  return context;
}

export function Root({
  children,
  color,
  value,
  onSelect,
  ...props
}: {
  children: React.ReactNode;
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  value: string;
  // Only needed by cards with interactive descendants (Action/AutoRun) that gate on selection —
  // clicking blank card space already selects the card natively, without this.
  onSelect?: () => void;
  'data-testid'?: string;
}) {
  const radioGroup = useRadioGroup();
  const selected = radioGroup?.value === value;

  // `onSelect` isn't guaranteed to be a stable reference (callers typically pass an inline
  // closure), so it's kept in a ref and read from there — that keeps `contextValue` from being
  // recomputed on every render just because the caller passed a new closure.
  const onSelectRef = React.useRef(onSelect);
  React.useLayoutEffect(() => {
    onSelectRef.current = onSelect;
  });

  const contextValue = React.useMemo(
    () => ({
      color,
      selected,
      selectCard: () => onSelectRef.current?.(),
    }),
    [color, selected]
  );

  return (
    <Card
      component="label"
      {...props}
      sx={(theme) => {
        const outlineColor = selected ? theme.palette[color].main : theme.palette.divider;

        return {
          height: '100%',
          minHeight: 184,
          padding: 2,
          cursor: 'pointer',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '1fr auto',
          gridTemplateRows: 'auto auto 1px minmax(0, 1fr)',
          gridTemplateAreas: `
            "count count"
            "content content"
            "divider divider"
            "action autoRun"
        `,
          position: 'relative',
          overflow: 'hidden',
          border: 0,
          bgcolor: 'background.default',
          backgroundImage: 'none',
          boxShadow: `${theme.shadows[selected ? 3 : 1]}, inset 0 0 0 0.5px ${outlineColor}`,
          opacity: selected ? 1 : 0.6,
          // The real focus target is the visually-hidden radio input below, so the ring is drawn
          // on the card via `:has()` instead of the (now unfocusable) card root.
          '&:has(:focus-visible)': {
            outline: '2px solid',
            outlineColor: theme.palette[color].main,
            outlineOffset: 2,
          },
        };
      }}
    >
      <Radio value={value} disableRipple sx={visuallyHidden} />

      <Context.Provider value={contextValue}>{children}</Context.Provider>
    </Card>
  );
}

export function Count(props: { value: React.ReactNode; color?: string }) {
  const { color } = useCardContext();

  return (
    <Typography
      component="p"
      variant="h4"
      color={props.color ?? `${color}.main`}
      sx={{ gridArea: 'count' }}
    >
      {props.value}
    </Typography>
  );
}

export function Adornment(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        position: 'absolute',
        // Matches the card's own padding so the adornment lines up with the rest of the content
        // instead of the card's border edge.
        top: theme.spacing(2),
        right: theme.spacing(2),
      })}
    >
      {props.children}
    </Box>
  );
}

export function Content(props: { label: React.ReactNode; emphasis?: React.ReactNode }) {
  const { color } = useCardContext();

  return (
    <Stack direction="column" spacing={0.5} sx={{ gridArea: 'content' }}>
      <Typography component="p" variant="body2">
        {props.label}
      </Typography>
      {props.emphasis ? (
        <Typography
          component="p"
          variant="caption"
          color={`${color}.main`}
          fontWeight={700}
          textTransform="uppercase"
        >
          {props.emphasis}
        </Typography>
      ) : null}
    </Stack>
  );
}

export function Divider() {
  const { color } = useCardContext();

  return (
    <Box
      sx={(theme) => ({
        gridArea: 'divider',
        alignSelf: 'stretch',
        minWidth: 0,
        bgcolor: theme.palette[color].main,
        opacity: 0.5,
      })}
    />
  );
}

export function Action(props: { children: React.ReactNode }) {
  return <Box sx={{ gridArea: 'action', alignSelf: 'center' }}>{props.children}</Box>;
}

/**
 * The card itself is selectable, so clicks inside the Auto-run control would otherwise bubble up
 * and change the selected bucket. Always stop propagation so callers can use this as a normal
 * form control without knowing about the card's selection behavior — while the card isn't
 * selected yet, `handleChange` selects it explicitly instead of relying on that bubbling.
 */
export function AutoRun(props: {
  checked: boolean;
  label?: React.ReactNode;
  onChange: () => void;
}) {
  const { selected, selectCard } = useCardContext();

  function stopCardSelection(event: React.MouseEvent) {
    event.stopPropagation();
  }

  function handleChange() {
    if (!selected) {
      selectCard();
      return;
    }

    props.onChange();
  }

  return (
    <Box sx={{ gridArea: 'autoRun', justifySelf: 'end', alignSelf: 'center' }}>
      <FormControlLabel
        control={
          <Switch
            checked={props.checked}
            size="small"
            onClick={stopCardSelection}
            onChange={handleChange}
            // One-off. The shared Switch fills its track solidly even when unchecked, which reads
            // as too prominent against this card's dark background. Mute it here rather than in
            // the shared component — this only covers the unchecked state, since Auto can't be
            // turned on yet.
            sx={{
              '& .MuiSwitch-track': {
                backgroundColor: 'transparent',
                border: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: 'neutral.light',
              },
            }}
          />
        }
        label={props.label ?? 'Auto'}
        labelPlacement="start"
        onClick={stopCardSelection}
        // @mend/mui Switch removes MUI's default switch padding, so FormControlLabel's normal
        // label/control separation collapses here. Keep this local until the shared switch can be
        // audited across its existing usages.
        sx={{
          [`&.${formControlLabelClasses.root}`]: { mx: 0 },
          [`& .${formControlLabelClasses.label}`]: { mr: 0.75 },
        }}
        slotProps={{ typography: { variant: 'caption' } }}
      />
    </Box>
  );
}

export function Status(props: { children: React.ReactNode }) {
  const { color } = useCardContext();

  return (
    <Typography component="span" variant="caption" color={`${color}.main`} fontWeight={600}>
      {props.children}
    </Typography>
  );
}
